/**
 * SecurityDashboardPage — `/security`.
 *
 * Surfaces the operator-side view of platform security: RBAC matrix
 * observability (F-124 / F-130), recent audit events, and the audit-chain
 * integrity strip.
 *
 * Wired endpoints:
 *   - `GET /v1/identity/stats/dashboard` — KPI counters (Identity/readStats).
 *   - `GET /v1/audit/integrity/all` — chain-verify health (F-137).
 *   - `GET /v1/audit/recent?limit=8` — most recent audit events (B3 slice 3).
 *
 * Compliance items reflect what the platform can actually attest right now:
 *   - audit-chain integrity (F-137) — live verify result
 *   - retention archive (F-138) — ON via the existing AuditRetention controller
 *   - operator-action audit trail — wired
 *   - per-field grant matrix (F-387) — wired via /security/field-permissions
 */
import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  KeyRound,
  Lock,
  LogIn,
  Shield,
  Users,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { listRecentAuditLogs, verifyAllChains } from '../../lib/api/audit';
import { getIdentityDashboardStats } from '../../lib/api/identity-admin';
import { PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

interface ComplianceItem {
  readonly label: string;
  readonly status: 'ok' | 'warn' | 'unknown';
  readonly detail: string;
}

export function SecurityDashboardPage(): JSX.Element {
  const integrity = useQuery({
    queryKey: ['audit', 'integrity', 'all'],
    queryFn: () => verifyAllChains(),
    retry: false,
  });

  const stats = useQuery({
    queryKey: ['identity', 'stats', 'dashboard'],
    queryFn: getIdentityDashboardStats,
    retry: false,
  });

  const recentEvents = useQuery({
    queryKey: ['audit', 'recent', { limit: 8 }],
    queryFn: () => listRecentAuditLogs({ limit: 8 }),
    retry: false,
  });

  const denied =
    integrity.isError && integrity.error instanceof ApiError &&
    (integrity.error.status === 401 || integrity.error.status === 403);

  const reports = integrity.data ?? [];
  const totalChains = reports.length;
  const okChains = reports.filter((r) => r.ok).length;
  const totalRows = reports.reduce((acc, r) => acc + r.totalRows, 0);
  const totalBreaks = reports.reduce((acc, r) => acc + r.breaks.length, 0);

  const fmt = (n: number | undefined): string =>
    typeof n === 'number' ? n.toLocaleString('en-US') : '—';

  const complianceItems: ComplianceItem[] = [
    {
      label: 'Audit-chain integrity',
      status: integrity.isPending
        ? 'unknown'
        : totalBreaks === 0 && totalChains > 0
          ? 'ok'
          : 'warn',
      detail:
        integrity.isPending
          ? 'Verifying…'
          : totalChains === 0
            ? 'No chains observed yet.'
            : `${okChains} / ${totalChains} chains OK; ${totalBreaks} break(s).`,
    },
    {
      label: 'Operator action audit trail',
      status: recentEvents.isError ? 'unknown' : 'ok',
      detail: recentEvents.isError
        ? 'Audit service temporarily unavailable.'
        : `${recentEvents.data?.length ?? 0} recent audit events.`,
    },
    {
      label: 'Per-field grant matrix',
      status: 'ok',
      detail: 'Per-field role grants are persisted.',
    },
    {
      label: 'Retention archive',
      status: 'ok',
      detail: 'On-demand retention archive runs are supported.',
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Security & Compliance"
        description="Platform security overview, RBAC matrix observability, and audit-chain health."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/security/users"
              className="px-3 py-2 text-xs font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light transition-colors"
            >
              Users
            </Link>
            <Link
              to="/security/roles"
              className="px-3 py-2 text-xs font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light transition-colors"
            >
              Roles
            </Link>
            <Link
              to="/security/field-permissions"
              className="px-3 py-2 text-xs font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light transition-colors"
            >
              Field permissions
            </Link>
            <Link
              to="/security/audit-log"
              className="px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors"
            >
              Audit log
            </Link>
          </div>
        }
      />

      <div data-testid="identity-kpis" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard
          title="Active users"
          value={fmt(stats.data?.activeUsers)}
          hint="user_account.status='ACTIVE'"
          icon={Users}
        />
        <KPICard
          title="Active sessions"
          value={fmt(stats.data?.activeSessions)}
          hint="non-revoked refresh tokens"
          icon={Activity}
        />
        <KPICard
          title="Failed logins (24h)"
          value="—"
          hint="login_attempt history not modelled"
          icon={LogIn}
        />
        <KPICard
          title="Locked accounts"
          value={fmt(stats.data?.lockedAccounts)}
          hint="user_account.status='LOCKED'"
          icon={Lock}
        />
        <KPICard
          title="Platform admins"
          value={fmt(stats.data?.platformAdmins)}
          hint="user_account.is_platform_admin"
          icon={AlertTriangle}
        />
        <KPICard
          title="Total roles"
          value={fmt(stats.data?.totalRoles)}
          hint="role catalogue"
          icon={KeyRound}
        />
      </div>

      {stats.isError ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          Identity stats unavailable: {(stats.error as Error).message}
        </div>
      ) : null}

      {/* Audit-chain integrity strip — wired to GET /v1/audit/integrity/all. */}
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-4 h-4 text-nipost-green" />
              Audit-chain integrity
            </h3>
            <p className="text-xs text-muted">
              On-demand hash-chain verify against <code className="font-mono">audit_log</code>.
            </p>
          </div>
          <Link
            to="/security/audit-log"
            className="text-xs font-semibold text-nipost-green hover:underline"
          >
            View full log →
          </Link>
        </div>

        {integrity.isPending ? (
          <p className="text-sm text-muted">Verifying chains…</p>
        ) : denied ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <code className="font-mono">AuditIntegrity/verify</code> permission required
            (AUDITOR / SYSTEM_ADMIN).
          </div>
        ) : integrity.isError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            Audit service temporarily unavailable: {(integrity.error as Error).message}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[11px] text-muted uppercase tracking-wider mb-1">Chains verified</p>
              <p className="text-lg font-bold text-gray-900">{totalChains.toLocaleString('en-US')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[11px] text-muted uppercase tracking-wider mb-1">Chains OK</p>
              <p className={`text-lg font-bold ${okChains === totalChains ? 'text-nipost-green' : 'text-amber-700'}`}>
                {okChains.toLocaleString('en-US')} / {totalChains.toLocaleString('en-US')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[11px] text-muted uppercase tracking-wider mb-1">Rows checked</p>
              <p className="text-lg font-bold text-gray-900">{totalRows.toLocaleString('en-US')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[11px] text-muted uppercase tracking-wider mb-1">Breaks</p>
              <p className={`text-lg font-bold ${totalBreaks === 0 ? 'text-nipost-green' : 'text-red-600'}`}>
                {totalBreaks.toLocaleString('en-US')}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div
          data-testid="recent-events"
          className="lg:col-span-2 bg-white border border-border rounded-xl p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Recent security events</h3>
            <Link
              to="/security/audit-log"
              className="text-xs font-semibold text-nipost-green hover:underline"
            >
              View full log →
            </Link>
          </div>
          {recentEvents.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : recentEvents.isError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              Audit service temporarily unavailable: {(recentEvents.error as Error).message}
            </div>
          ) : (recentEvents.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted">No audit events recorded yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {(recentEvents.data ?? []).map((entry) => (
                <li
                  key={entry.id}
                  data-testid={`recent-event-${entry.id}`}
                  className="flex items-start gap-3 py-2"
                >
                  <Activity className="w-3 h-3 text-muted mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs">
                      <span className="font-mono text-gray-900">
                        {entry.action ?? entry.ceType}
                      </span>
                      {entry.module ? (
                        <span className="text-muted"> · {entry.module}</span>
                      ) : null}
                      {entry.entityType ? (
                        <span className="text-muted"> · {entry.entityType}/{entry.entityId ?? '?'}</span>
                      ) : null}
                    </p>
                    <p className="text-[10px] text-muted">
                      {new Date(entry.receivedAt).toLocaleString('en-GB')}
                      {entry.actorUserId ? ` · actor=${entry.actorUserId.slice(0, 8)}…` : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Compliance status</h3>
          <ul className="space-y-3" data-testid="compliance-list">
            {complianceItems.map((item) => (
              <li
                key={item.label}
                data-testid={`compliance-${item.status}`}
                className={`flex items-start gap-3 p-3 rounded-lg ${
                  item.status === 'ok'
                    ? 'bg-nipost-green-light'
                    : item.status === 'warn'
                      ? 'bg-amber-50'
                      : 'bg-gray-50'
                }`}
              >
                <CheckCircle
                  className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                    item.status === 'ok'
                      ? 'text-nipost-green'
                      : item.status === 'warn'
                        ? 'text-amber-600'
                        : 'text-muted'
                  }`}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.label}</p>
                  <p className="text-xs text-muted mt-0.5">{item.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
